/**
 * The exam runner's state: paper, answers, timer, devices, autosave.
 *
 * Design notes worth keeping:
 *
 *  - The paper is fetched ONCE (`?paper=1`). Every subsequent poll passes
 *    `?paper=0`, because the questions cannot change mid-attempt and re-sending
 *    a dozen markdown bodies every few seconds for three hours is pure waste.
 *
 *  - The countdown is driven by `deadline_ms` + the ServerClock, not by a local
 *    `setInterval` decrement. It ticks for display only; the authoritative
 *    value is re-synced on every poll, so a laptop that sleeps for ten minutes
 *    wakes up showing the correct remaining time rather than ten minutes too
 *    much.
 *
 *  - Answers go out immediately via AnswerQueue and survive reloads; visits go
 *    out via VisitQueue. Neither can lose data to a network drop.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AnswerResponse,
  AttemptPaperItem,
  AttemptResultResponse,
  AttemptStage,
  AttemptStateResponse,
  AttemptStatus,
  DeviceStatus,
  MediaCounts,
} from "@exam/shared";
import { PHONE_HEARTBEAT_TIMEOUT_MS } from "@exam/shared";
import {
  getAttempt,
  heartbeat,
  submitAttempt as submitAttemptRequest,
} from "@/lib/exam-api";
import { ServerClock, timed } from "./serverClock";
import { AnswerQueue, type AnswerQueueState } from "./answerQueue";
import { VisitQueue } from "./visitQueue";
import { countAnswers, type AnswerCounts } from "./answerState";

/** How often the laptop re-syncs state. Also its heartbeat cadence. */
const POLL_INTERVAL_MS = 10_000;
/** Display tick. Cheap — it reads the clock, it does not decrement a counter. */
const TICK_MS = 250;

export interface UseAttemptOptions {
  attemptId: string;
  /** Polling and autosave stay off until the exam is actually running. */
  active?: boolean;
}

export interface AttemptHandle {
  loading: boolean;
  error: string | null;

  paper: AttemptPaperItem[];
  exam: AttemptStateResponse["exam"] | null;
  status: AttemptStatus;
  stage: AttemptStage;
  startedAtMs: number | null;

  responses: Map<string, AnswerResponse | null>;
  markedForReview: Set<string>;
  visited: Set<string>;
  counts: AnswerCounts;

  /** Milliseconds left, from the server's deadline. Null before the start. */
  remainingMs: number | null;
  totalMs: number | null;
  /**
   * Absolute epoch ms when a manual submit becomes allowed, or null when submit
   * is unlocked (no minimum) / the attempt has not started. The runner counts
   * down to this; the server enforces it.
   */
  submitUnlocksAtMs: number | null;
  /** True under five minutes — the timer turns amber. */
  timeCritical: boolean;
  expired: boolean;

  devices: DeviceStatus[];
  phoneAlive: boolean;
  mediaCounts: MediaCounts;
  totalViolations: number;

  save: AnswerQueueState;
  clock: ServerClock;

  /** Records the answer for a question (null clears it). Saves immediately. */
  setResponse: (questionId: string, response: AnswerResponse | null) => void;
  setMarkedForReview: (questionId: string, marked: boolean) => void;
  /** Call whenever the visible question changes. Records the visit offset. */
  visitQuestion: (questionId: string) => void;
  /** Flushes every queue, then submits. */
  submit: () => Promise<AttemptResultResponse>;
  refresh: () => Promise<void>;
}

export function useAttempt(options: UseAttemptOptions): AttemptHandle {
  const { attemptId, active = true } = options;

  const clock = useMemo(() => new ServerClock(), []);
  const answerQueueRef = useRef<AnswerQueue | null>(null);
  const visitQueueRef = useRef<VisitQueue | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [paper, setPaper] = useState<AttemptPaperItem[]>([]);
  const [exam, setExam] = useState<AttemptStateResponse["exam"] | null>(null);
  const [status, setStatus] = useState<AttemptStatus>("not_started");
  const [stage, setStage] = useState<AttemptStage>("idle");
  const [startedAtMs, setStartedAtMs] = useState<number | null>(null);
  const [deadlineMs, setDeadlineMs] = useState<number | null>(null);
  const [totalMs, setTotalMs] = useState<number | null>(null);
  const [submitUnlocksAtMs, setSubmitUnlocksAtMs] = useState<number | null>(
    null,
  );

  const [responses, setResponses] = useState<Map<string, AnswerResponse | null>>(
    new Map(),
  );
  const [markedForReview, setMarked] = useState<Set<string>>(new Set());
  const [visited, setVisited] = useState<Set<string>>(new Set());

  const [devices, setDevices] = useState<DeviceStatus[]>([]);
  const [mediaCounts, setMediaCounts] = useState<MediaCounts>({});
  const [totalViolations, setTotalViolations] = useState(0);

  const [save, setSave] = useState<AnswerQueueState>({
    pending: 0,
    saving: false,
    degraded: false,
    lastSavedAtMs: null,
    lastErrorMessage: null,
  });

  // Ticked for display; the real value comes from `deadlineMs` + the clock.
  const [, forceTick] = useState(0);

  const paperLoadedRef = useRef(false);
  const currentQuestionRef = useRef<{ id: string; sinceMs: number } | null>(
    null,
  );

  /* -- queues ------------------------------------------------------- */

  useEffect(() => {
    const answers = new AnswerQueue({
      attemptId,
      onChange: setSave,
      onAttemptClosed: (message) => setError(message),
    });
    const visits = new VisitQueue(attemptId);
    answerQueueRef.current = answers;
    visitQueueRef.current = visits;

    return () => {
      answerQueueRef.current = null;
      visitQueueRef.current = null;
      answers.dispose();
      visits.dispose();
    };
  }, [attemptId]);

  /* -- state ingestion ---------------------------------------------- */

  const ingest = useCallback((state: AttemptStateResponse) => {
    setExam(state.exam);
    setStatus(state.attempt.status);
    setStage(state.stage);
    setStartedAtMs(state.attempt.started_at);
    setDeadlineMs(state.deadline_ms);
    setTotalMs(state.exam.duration_min * 60_000);
    setSubmitUnlocksAtMs(state.submit_unlocks_at_ms ?? null);
    setDevices(state.devices);
    setMediaCounts(state.media_counts);
    setTotalViolations(state.total_violations);

    if (state.paper) {
      setPaper([...state.paper].sort((a, b) => a.order_idx - b.order_idx));
      paperLoadedRef.current = true;
    }

    // Server answers are the source of truth for anything NOT queued locally.
    // A locally-pending edit must win, or a poll landing between "typed" and
    // "saved" would visibly revert the student's answer under their cursor.
    const pendingIds = new Set(
      answerQueueRef.current?.pendingQuestionIds() ?? [],
    );

    setResponses((prev) => {
      const next = new Map(prev);
      for (const answer of state.answers) {
        if (pendingIds.has(answer.question_id)) continue;
        next.set(answer.question_id, answer.response);
      }
      return next;
    });

    setMarked((prev) => {
      const next = new Set(prev);
      for (const answer of state.answers) {
        if (pendingIds.has(answer.question_id)) continue;
        if (answer.marked_for_review) next.add(answer.question_id);
        else next.delete(answer.question_id);
      }
      return next;
    });

    // A question with any recorded visit has been seen, even in an earlier
    // session — so the palette is right after a reload.
    setVisited((prev) => {
      const next = new Set(prev);
      for (const answer of state.answers) {
        if (answer.visit_count > 0) next.add(answer.question_id);
      }
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const state = await timed(clock, () =>
        getAttempt(attemptId, { includePaper: !paperLoadedRef.current }),
      );
      ingest(state);
      setError(null);
    } catch (err) {
      // A failed poll is not fatal — the countdown keeps running off the last
      // known deadline and the next poll will correct it.
      console.warn("[exam] state poll failed", err);
    }
  }, [attemptId, clock, ingest]);

  /* -- initial load -------------------------------------------------- */

  useEffect(() => {
    let cancelled = false;
    paperLoadedRef.current = false;

    (async () => {
      setLoading(true);
      try {
        const state = await timed(clock, () =>
          getAttempt(attemptId, { includePaper: true }),
        );
        if (cancelled) return;
        ingest(state);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Could not load the exam.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attemptId, clock, ingest]);

  /* -- polling + laptop heartbeat ------------------------------------ */

  useEffect(() => {
    if (!active) return;

    const poll = async () => {
      await refresh();
      try {
        // The laptop must beat too: the server uses it to show the admin that
        // this device is alive, exactly as it does for the phone.
        await timed(clock, () => heartbeat(attemptId, "laptop"));
      } catch {
        /* the state poll already reported connectivity */
      }
    };

    const id = setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active, attemptId, clock, refresh]);

  /* -- display tick --------------------------------------------------- */

  useEffect(() => {
    if (!active || deadlineMs === null) return;
    const id = setInterval(() => forceTick((n) => n + 1), TICK_MS);
    return () => clearInterval(id);
  }, [active, deadlineMs]);

  /* -- actions -------------------------------------------------------- */

  const setResponse = useCallback(
    (questionId: string, response: AnswerResponse | null) => {
      setResponses((prev) => new Map(prev).set(questionId, response));
      answerQueueRef.current?.enqueue({ questionId, response });
    },
    [],
  );

  const setMarkedForReview = useCallback(
    (questionId: string, marked: boolean) => {
      setMarked((prev) => {
        const next = new Set(prev);
        if (marked) next.add(questionId);
        else next.delete(questionId);
        return next;
      });

      // The response must be re-sent with the flag: this endpoint upserts the
      // whole answer row, so omitting it would clear what the student chose.
      setResponses((current) => {
        answerQueueRef.current?.enqueue({
          questionId,
          response: current.get(questionId) ?? null,
          markedForReview: marked,
        });
        return current;
      });
    },
    [],
  );

  const visitQuestion = useCallback(
    (questionId: string) => {
      const previous = currentQuestionRef.current;
      if (previous?.id === questionId) return;

      const nowMs = clock.now();
      const offsetMs = clock.offsetFrom(startedAtMs);
      const timeSpentMs = previous ? Math.max(0, nowMs - previous.sinceMs) : 0;

      visitQueueRef.current?.record(
        questionId,
        offsetMs,
        previous?.id ?? null,
        timeSpentMs,
      );

      currentQuestionRef.current = { id: questionId, sinceMs: nowMs };
      setVisited((prev) =>
        prev.has(questionId) ? prev : new Set(prev).add(questionId),
      );
    },
    [clock, startedAtMs],
  );

  const submit = useCallback(async () => {
    // Flush first, in order: an answer typed one second before pressing Submit
    // must be in the database before the server scores the attempt.
    await answerQueueRef.current?.finish();
    await visitQueueRef.current?.finish();
    const result = await submitAttemptRequest(attemptId);
    setStatus(result.attempt.status);
    return result;
  }, [attemptId]);

  /* -- derived --------------------------------------------------------- */

  const remainingMs = clock.remainingFrom(deadlineMs);

  const counts = useMemo(
    () =>
      countAnswers(
        paper.map((item) => item.question.id),
        responses,
        markedForReview,
      ),
    [paper, responses, markedForReview],
  );

  const phoneAlive = useMemo(() => {
    const phone = devices.find((d) => d.role === "phone");
    if (!phone?.paired) return false;
    if (phone.alive) return true;
    // Fall back to the timestamp when the server's flag is stale between polls.
    return (
      phone.last_heartbeat_at !== null &&
      clock.now() - phone.last_heartbeat_at < PHONE_HEARTBEAT_TIMEOUT_MS
    );
  }, [devices, clock]);

  return {
    loading,
    error,
    paper,
    exam,
    status,
    stage,
    startedAtMs,
    responses,
    markedForReview,
    visited,
    counts,
    remainingMs,
    totalMs,
    submitUnlocksAtMs,
    timeCritical: remainingMs !== null && remainingMs <= 5 * 60_000,
    expired: remainingMs !== null && remainingMs <= 0,
    devices,
    phoneAlive,
    mediaCounts,
    totalViolations,
    save,
    clock,
    setResponse,
    setMarkedForReview,
    visitQuestion,
    submit,
    refresh,
  };
}
